import { formatarValorMonetarioInputNumerico } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { mensagemDeErroPadrao } from "shared/util/functions/yup/MensagensDeErroInput";
import { RegistroContaProvisionamentoSelectAsyncDTO } from "types/dto/contaProvisionamento/RegistroContaProvisionamentoSelectAsyncDTO";
import { RegistroPlanoContasSelectAsyncDTO } from "types/dto/contasPagar/RegistroPlanoContasSelectAsyncDTO";
import { RegistroFornecedorSelectAsyncDTO } from "types/dto/fornecedor/RegistroFornecedorSelectAsyncDTO";
import { RegistroNaturezaRendimentoSelectAsyncDTO } from "types/dto/naturezaRendimento/RegistroNaturezaRendimentoSelectAsyncDTO";
import { RegistroCondominioDTO } from "types/dto/RegistroCondominioDTO";
import { SelectOptionType } from "types/select/SelectOptionType";
import * as yup from "yup";
import { CadastrarDespesaRetencaoGroupPaySchema } from "./CadastrarDespesaRetencaoGroupPaySchema";
import { AnexoItem } from "shared/components/input/InputMultiAnexos";

const toNumber = (v: unknown): number => {
  if (v == null) return 0;
  const num = typeof v === "number" ? v : formatarValorMonetarioInputNumerico(String(v));
  return isNaN(num) ? 0 : num;
};

const somarValores = (
  itens: Record<string, unknown>[],
  key: "valorBruto" | "valor"
): number => itens.reduce((total, item) => total + toNumber(item[key]), 0);

export const CadastrarDespesaUnicaGroupPaySchema = yup.object().shape({
  condominio: yup.mixed<SelectOptionType<RegistroCondominioDTO>>().required(mensagemDeErroPadrao("Condomínio")),

  isFavorecidoFornecedor: yup.boolean(),

  favorecido: yup
    .mixed<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>()
    .nullable()
    .when("isFavorecidoFornecedor", {
      is: true,
      then: (schema) =>
        schema
          .required(mensagemDeErroPadrao("Favorecido"))
          .test("favorecido-obrigatorio", mensagemDeErroPadrao("Favorecido"), (opcao) => !!opcao?.value?.fornecedorId),
    }),

  codigoDeBarras: yup.string().nullable().notRequired(),

  dataVencimento: yup.date().required(mensagemDeErroPadrao("Data de vencimento")).typeError("Informe uma data válida."),

  fornecedor: yup
    .mixed<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>()
    .required(mensagemDeErroPadrao("Fornecedor"))
    .test("fornecedor-obrigatorio", mensagemDeErroPadrao("Fornecedor"), (opcao) => {
      return !!opcao?.value?.nomeFornecedor || !!opcao?.value?.fornecedorId
    }),

  conta: yup
    .mixed<SelectOptionType<number>>()
    .nullable()
    .when("dataPagamento", {
      is: (dataPagamento: Date | null) => !!dataPagamento,
      then: (schema) =>
        schema
          .required(mensagemDeErroPadrao("Conta"))
          .test("conta-obrigatoria", mensagemDeErroPadrao("Conta"), (opcao) => !!opcao?.value),
    }),

  classesConta: yup
    .array()
    .of(
      yup.object().shape({
        classePlano: yup
          .mixed<SelectOptionType<RegistroPlanoContasSelectAsyncDTO>>()
          .required(mensagemDeErroPadrao("Classe de conta"))
          .test(
            "classe-plano-obrigatorio",
            mensagemDeErroPadrao("Classe de conta"),
            (opcao) => !!opcao?.value?.idClassePlano
          ),
        valorBruto: yup
          .number()
          .transform((_, originalValue) => {
            let valor = originalValue ? originalValue : "0";
            return typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(valor);
          })
          .required(mensagemDeErroPadrao("Valor bruto"))
          .typeError("Informe um valor válido")
          .positive("O valor bruto deve ser maior do que 0!"),
        contaProvisionamento: yup
          .mixed<SelectOptionType<RegistroContaProvisionamentoSelectAsyncDTO>>()
          .nullable()
          .when("condominioPossuiContaProvisionamentoAtiva", {
            is: true,
            then: (schema) => schema.required(mensagemDeErroPadrao("Conta de provisionamento")),
            otherwise: (schema) => schema,
          }),
        descricao: yup.string().max(150, "A descrição deve ter no máximo 150 caracteres.").nullable().notRequired(),
      })
    )
    .required(),

  mesCompetencia: yup.date().required(mensagemDeErroPadrao("Mês competência")).typeError("Informe uma data válida."),

  naturezaRendimento: yup.mixed<SelectOptionType<RegistroNaturezaRendimentoSelectAsyncDTO>>().nullable(),

  retencoes: yup
    .array()
    .of(CadastrarDespesaRetencaoGroupPaySchema)
    .default([])
    .test(
      "somaMenorOuIgualBruto",
      "O somatório dos valores informados para retenções não pode ser superior ao valor bruto total.",
      function (value) {
        if (!value?.length) return true;

        const classesConta = (this.parent?.classesConta ?? []) as Record<string, unknown>[];
        const valorBrutoTotal = somarValores(classesConta, "valorBruto");
        const somaRetencoes = somarValores(value as Record<string, unknown>[], "valor");

        return somaRetencoes <= valorBrutoTotal;
      }
    ),

  numeroDocumento: yup.string().nullable().notRequired(),
  serieNf: yup.string().nullable().notRequired(),
  dataEmissao: yup.date().nullable().notRequired(),
  observacao: yup.string().nullable().notRequired(),
  tipoFormaPagamento: yup
    .mixed<SelectOptionType<string>>()
    .nullable()
    .when("dataPagamento", {
      is: (dataPagamento: Date | null) => !!dataPagamento,
      then: (schema) => schema.required(mensagemDeErroPadrao("Forma de pagamento")),
    }),

  dataPagamento: yup
    .date()
    .nullable()
    .typeError("Informe uma data válida.")
    .when(["juros", "multa", "desconto"], ([valorJuros, valorMulta, valorDesconto], schema) => {
      const converterParaNumero = (valor: any) => (valor ? formatarValorMonetarioInputNumerico(String(valor)) : 0);
      const possuiAcrescimosOuDesconto =
        converterParaNumero(valorJuros) > 0 ||
        converterParaNumero(valorMulta) > 0 ||
        converterParaNumero(valorDesconto) > 0;
      return possuiAcrescimosOuDesconto ? schema.required(mensagemDeErroPadrao("Data de pagamento")) : schema;
    }),

  dataLiquidacao: yup
    .date()
    .nullable()
    .typeError("Informe uma data válida.")
    .when(["dataPagamento", "juros", "multa", "desconto"], ([dataPagamentoInformada, valorJuros, valorMulta, valorDesconto], schema) => {
      const converterParaNumero = (valor: any) => (valor ? formatarValorMonetarioInputNumerico(String(valor)) : 0);
      const possuiAcrescimosOuDesconto =
        converterParaNumero(valorJuros) > 0 ||
        converterParaNumero(valorMulta) > 0 ||
        converterParaNumero(valorDesconto) > 0;
      const schemaComObrigatoriedade =
        dataPagamentoInformada || possuiAcrescimosOuDesconto
          ? schema.required(mensagemDeErroPadrao("Data de liquidação"))
          : schema;
      return dataPagamentoInformada
        ? schemaComObrigatoriedade.min(
            yup.ref("dataPagamento"),
            "A data de liquidação não pode ser inferior à data de pagamento."
          )
        : schemaComObrigatoriedade;
    }),

  incluirAcrescimosDescontos: yup.boolean().notRequired(),
  juros: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  multa: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  desconto: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  valorPago: yup.number().nullable(),
  valorLiquido: yup.number().nullable(),

  tipoFormaPagamentoPix: yup
    .string()
    .nullable()
    .when(["tipoFormaPagamento"], ([tipo], schema) => {
      const isPix = tipo?.value === "PIX";
      return isPix
        ? schema
          .required("Escolha uma forma de pagamento PIX")
          .oneOf(["TRANSFERENCIA", "QR_CODE"], "Selecione uma opção válida.")
        : schema;
    }),
  codigoCopiaColaPix: yup
    .string()
    .nullable()
    .when(["tipoFormaPagamentoPix", "dataPagamento"], ([tipoPix, dataPagamento], schema) => {
      return tipoPix === "QR_CODE" && !!dataPagamento
        ? schema.required(mensagemDeErroPadrao("Código Copia e Cola PIX"))
        : schema;
    }),

  numeroCheque: yup
    .string()
    .nullable()
    .when(["tipoFormaPagamento", "dataPagamento"], ([tipo, dataPagamento], schema) => {
      return tipo?.value === "CHEQUE" && !!dataPagamento ? schema.required(mensagemDeErroPadrao("Número do cheque")) : schema;
    }),
  anexos: yup.mixed<AnexoItem[]>(),
});
